import { motion } from 'framer-motion';
import { Award, Factory, Users, Target } from 'lucide-react';
import ClientsVendorsSection from '../components/ClientsVendorsSection';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality First',
      desc: 'We never compromise on fabric quality or craftsmanship.',
    },
    {
      icon: Factory,
      title: 'Manufacturing Excellence',
      desc: 'State-of-the-art facilities with experienced craftspeople.',
    },
    {
      icon: Users,
      title: 'Client Partnership',
      desc: 'We build long-term relationships, not one-off transactions.',
    },
    {
      icon: Target,
      title: 'Sustainability',
      desc: 'Ethical manufacturing practices and eco-conscious materials.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="about-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="about-hero-heading">
              About Rudra Apparel
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Manufacturing premium uniforms and apparel for over 15 years, trusted by institutions and individuals across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-spacing" data-testid="about-story">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-brand-walnut leading-relaxed">
                <p>
                  Rudra Apparel began in 2008 with a simple mission: to provide high-quality, durable uniforms that institutions could rely on. What started as a small workshop in Mumbai has grown into a full-scale manufacturing facility serving 500+ clients nationwide.
                </p>
                <p>
                  We've dressed corporate offices, educational institutions, sports teams, and industrial workforces. Our commitment to quality, timely delivery, and customer satisfaction has made us a trusted name in uniform manufacturing.
                </p>
                <p>
                  Today, we combine traditional tailoring expertise with modern manufacturing technology to produce both bespoke uniforms and ready-to-wear collections.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="parallax-img rounded-sm overflow-hidden border border-brand-pearl/10">
                <img
                  src="https://images.unsplash.com/photo-1758270804188-8ca0b6d254bc?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Our facility"
                  className="w-full h-96 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="about-values">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4">
              Our Values
            </h2>
            <p className="text-base sm:text-lg text-brand-walnut max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-brand-ivory rounded-sm border border-brand-pearl/10 p-6 text-center"
                  data-testid={`value-${idx}`}
                >
                  <div className="w-16 h-16 bg-brand-pearl rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-brand-ivory" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-brand-walnut">{value.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="section-spacing" data-testid="about-infrastructure">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4">
              Manufacturing Infrastructure
            </h2>
            <p className="text-base sm:text-lg text-brand-walnut max-w-2xl mx-auto">
              Our facility is equipped to handle projects of any scale.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="infra-stat-0">
              <div className="text-4xl font-heading font-bold text-brand-pearl mb-2">25,000</div>
              <p className="text-brand-walnut">Sq. Ft. Production Facility</p>
            </div>
            <div className="text-center" data-testid="infra-stat-1">
              <div className="text-4xl font-heading font-bold text-brand-pearl mb-2">100+</div>
              <p className="text-brand-walnut">Skilled Craftspeople</p>
            </div>
            <div className="text-center" data-testid="infra-stat-2">
              <div className="text-4xl font-heading font-bold text-brand-pearl mb-2">50K+</div>
              <p className="text-brand-walnut">Units Per Month Capacity</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clients & Vendors */}
      <ClientsVendorsSection />

      {/* Vision */}
      <section className="section-spacing bg-brand-pearl text-brand-ivory" data-testid="about-vision">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
            Our Vision
          </h2>
          <p className="text-base sm:text-lg max-w-3xl mx-auto opacity-90 leading-relaxed">
            To be India's most trusted uniform manufacturer, known for quality, reliability, and innovation. We're expanding into new categories while maintaining the craftsmanship that built our reputation.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;